<!--
  Icon button that opens a popup menu of grouped actions — the
  ContactPanel bottom strip's Copy / Email / Text link menus. Same neutral
  styling as ActionToolbar; green check while `done` is set.

  sections: [{ header?, items: [{ key, label, icon?, onClick, disabled?, title?, visible?, loadingLabel? }] }]
  `buttonClass` replaces the bordered button styling; `down` opens the menu below the button.
  An item whose key equals `busyKey` shows its loadingLabel and is disabled.
-->
<template>
  <div class="relative" ref="rootRef">
    <button
      @click.stop="open = !open"
      :title="title"
      :disabled="disabled"
      class="disabled:opacity-40 disabled:cursor-not-allowed"
      :class="buttonClass
        ? [buttonClass, done && '!text-green-600 dark:!text-green-400']
        : ['h-8 w-8 flex items-center justify-center rounded-md border transition-colors', done
          ? 'border-green-500 dark:border-green-600 text-green-600 dark:text-green-400'
          : 'border-stroke-strong dark:border-stroke-strong-dark text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface dark:hover:bg-surface-dark']"
    >
      <component :is="done ? CheckIcon : icon" class="h-4 w-4 flex-shrink-0" />
    </button>
    <div
      v-if="open"
      class="absolute right-0 w-60 max-h-[70vh] overflow-y-auto rounded-lg bg-surface dark:bg-surface-dark shadow-lg ring-1 ring-black/5 dark:ring-stroke-dark z-30 py-1"
      :class="down ? 'top-full mt-1' : 'bottom-full mb-1'"
      @click.stop
    >
      <template v-for="(section, i) in visibleSections" :key="i">
        <div v-if="i > 0" class="my-1 border-t border-stroke-subtle dark:border-stroke-dark"></div>
        <div v-if="section.header" class="px-3 py-1.5 text-xs font-medium text-fg-muted dark:text-fg-muted-dark">{{ section.header }}</div>
        <button
          v-for="item in section.items"
          :key="item.key"
          @click="run(item)"
          :disabled="item.disabled || busyKey === item.key"
          :title="item.title || ''"
          class="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <component v-if="item.icon" :is="item.icon" class="h-4 w-4 flex-shrink-0" />
          <span class="flex-1 truncate">{{ busyKey === item.key ? (item.loadingLabel || 'Working…') : item.label }}</span>
        </button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { CheckIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  icon:     { type: [Object, Function], required: true },
  title:    { type: String, default: '' },
  sections: { type: Array, required: true },
  busyKey:  { type: String, default: null },
  done:     { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  buttonClass: { type: String, default: '' },
  down:     { type: Boolean, default: false },
})

const visibleSections = computed(() => props.sections
  .map(s => ({ ...s, items: s.items.filter(i => i.visible !== false) }))
  .filter(s => s.items.length))

const open    = ref(false)
const rootRef = ref(null)

function run(item) {
  open.value = false
  item.onClick()
}

function handleOutsideClick(e) {
  if (rootRef.value && !rootRef.value.contains(e.target)) open.value = false
}
onMounted(() => document.addEventListener('click', handleOutsideClick))
onUnmounted(() => document.removeEventListener('click', handleOutsideClick))
</script>
